import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavbarComponent } from '../navbar/navbar.component';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RestService, UserTestListItem, UserTestResumeResponse, RecreateSettings, FriendSummary } from '../service/rest/rest.service';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { Subject } from '../model/subject';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-saved-tests',
  standalone: true,
  imports: [NavbarComponent, RouterLink, CommonModule, FormsModule],
  templateUrl: './saved-tests.component.html',
  styleUrl: './saved-tests.component.css'
})
export class SavedTestsComponent implements OnInit, OnDestroy {
  userTests: UserTestListItem[] = [];
  filteredTests: UserTestListItem[] = [];
  availableSubjects: Subject[] = [];
  selectedMode: string = 'all'; // 'all', 'test', or 'exam'
  selectedSubjectId: number | null = null;
  isLoading = false;
  sortColumn: string = 'savedDate'; // Default sort column
  sortDirection: 'asc' | 'desc' = 'desc'; // Default sort direction (descending for saved date)
  private routerSubscription?: Subscription;

  // Share dialog state
  shareDialogTestId: number | null = null;
  shareDialogTest: UserTestListItem | null = null;
  shareFriends: FriendSummary[] = [];
  shareFriendsLoading = false;
  shareUsername = '';
  shareSubmitLoading = false;
  shareStopLoading = false;

  // Share error dialog (e.g. no subscription)
  shareErrorMessage: string | null = null;

  constructor(
    private restService: RestService,
    private authService: AuthService,
    private router: Router
  ) {
    // Reload data whenever navigation ends on /saved-tests (handles same-URL and browser-back)
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url: string = event.urlAfterRedirects || event.url;
        if (url.includes('/saved-tests')) {
          this.restService.clearRecreateSettings();
          this.restService.clearCurrentTestState(); // so Resume/Recreate uses fresh data for the clicked test
          this.loadData();
        }
      });
  }

  async ngOnInit() {
    this.restService.clearRecreateSettings();
    this.restService.clearCurrentTestState();
    await this.loadData();
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  async loadData() {
    this.isLoading = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      // Load available subjects
      const subjectIds = await this.restService.getAvailableSubjects(sessionId);
      if (subjectIds && subjectIds.length > 0) {
        // Load subject details
        const allSubjects = await this.loadSubjectDetails(sessionId, subjectIds);
        this.availableSubjects = allSubjects;
      }

      // Load user tests
      await this.loadUserTests();
    } catch (error) {
      LoggerService.error('Failed to load data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadSubjectDetails(sessionId: string, subjectIds: number[]): Promise<Subject[]> {
    const subjects: Subject[] = [];
    // We need to get subjects from all courses
    const courses = await this.restService.getCourses(sessionId);
    if (courses) {
      for (const course of courses) {
        const courseSubjects = await this.restService.getSubjects(sessionId, parseInt(course.courseId));
        if (courseSubjects) {
          for (const subject of courseSubjects) {
            if (subjectIds.includes(subject.subjectId)) {
              subjects.push(subject);
            }
          }
        }
      }
    }
    return subjects;
  }

  async loadUserTests() {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        return;
      }

      // Ensure subjectId is a number or undefined (not string or null)
      let subjectIdForBackend: number | undefined = undefined;
      if (this.selectedSubjectId !== null && this.selectedSubjectId !== undefined) {
        const numId = typeof this.selectedSubjectId === 'string' 
          ? parseInt(this.selectedSubjectId, 10) 
          : Number(this.selectedSubjectId);
        if (!isNaN(numId)) {
          subjectIdForBackend = numId;
        }
      }

      const modeForBackend = this.selectedMode === 'all' ? undefined : this.selectedMode;
      const tests = await this.restService.getUserTests(sessionId, modeForBackend, subjectIdForBackend);
      if (tests) {
        this.userTests = tests;
        this.applyFilters();
      }
    } catch (error) {
      LoggerService.error('Failed to load user tests:', error);
    }
  }

  onModeChange() {
    this.loadUserTests();
  }

  onSubjectChange() {
    // Ensure selectedSubjectId is properly converted to number or null
    if (this.selectedSubjectId !== null && typeof this.selectedSubjectId === 'string') {
      this.selectedSubjectId = parseInt(this.selectedSubjectId, 10);
    }
    if (this.selectedSubjectId !== null && isNaN(this.selectedSubjectId)) {
      this.selectedSubjectId = null;
    }
    this.loadUserTests();
  }

  applyFilters() {
    this.filteredTests = this.userTests.filter(test => {
      if (this.selectedMode !== 'all' && test.mode !== this.selectedMode) {
        return false;
      }
      const selectedId = this.selectedSubjectId !== null ? Number(this.selectedSubjectId) : null;
      if (selectedId !== null && test.subjectIds != null && test.subjectIds.length > 0) {
        if (!test.subjectIds.some((id: number) => Number(id) === selectedId)) {
          return false;
        }
      } else if (selectedId !== null && (!test.subjectIds || test.subjectIds.length === 0)) {
        return false;
      }
      return true;
    });
    // Apply sorting after filtering
    this.applySorting();
  }

  applySorting() {
    this.filteredTests.sort((a, b) => {
      let comparison = 0;
      
      switch (this.sortColumn) {
        case 'subjectName':
          comparison = (a.subjectName || '').localeCompare(b.subjectName || '');
          break;
        case 'questionCount':
          comparison = (a.questionCount || 0) - (b.questionCount || 0);
          break;
        case 'completed':
          // Sort by completed count
          const aCompleted = a.completedCount || 0;
          const bCompleted = b.completedCount || 0;
          comparison = aCompleted - bCompleted;
          break;
        case 'score':
          const aScore = a.score ?? -1;
          const bScore = b.score ?? -1;
          comparison = aScore - bScore;
          break;
        case 'savedDate':
          const aDate = a.savedDate ? new Date(a.savedDate).getTime() : 0;
          const bDate = b.savedDate ? new Date(b.savedDate).getTime() : 0;
          comparison = aDate - bDate;
          break;
        default:
          return 0;
      }
      
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      // Toggle direction if same column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to ascending
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'bi-arrow-down-up'; // Neutral icon when not sorted
    }
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  async onDelete(testId: number) {
    if (!confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      return;
    }

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      const success = await this.restService.deleteUserTest(sessionId, testId);
      if (success) {
        // Reload tests
        await this.loadUserTests();
      } else {
        alert('Failed to delete test. Please try again.');
      }
    } catch (error) {
      LoggerService.error('Failed to delete test:', error);
      alert('An error occurred while deleting the test.');
    }
  }

  async onResume(testId: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        alert('Please log in to resume a test.');
        return;
      }
      this.restService.clearRecreateSettings();
      this.restService.clearCurrentTestState();
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test data. The test may have been deleted.');
        return;
      }

      console.log('[onResume] Resume data:', resumeData);

      // Set up RestService state
      this.restService.currentUserTestId = resumeData.userTestId;
      this.restService.currentUserTestMode = resumeData.mode;
      this.restService.courseId = resumeData.courseId;
      this.restService.subjectIds = resumeData.subjectIds ?? [];

      if (resumeData.mode === 'exam' && resumeData.subjectIds?.length) {
        const subjects = await this.restService.getSubjects(sessionId, resumeData.courseId);
        const firstSubject = subjects?.find(s => s.subjectId === resumeData.subjectIds![0]);
        if (firstSubject?.examTimeMinutes) {
          this.restService.currentExamTimeMinutes = firstSubject.examTimeMinutes;
        }
      }

      // Extract all question IDs from resume data (both answered and unanswered)
      const allQuestionIds = resumeData.questions.map(q => q.questionId);
      if (allQuestionIds.length === 0) {
        alert('No questions found for this test.');
        return;
      }

      // Set questionIds and initialize questionsMap with all questions
      this.restService.setQuestionIdsForResume(allQuestionIds);
      this.restService.setMaxQuestions(allQuestionIds.length);

      // Create answer map for restoring state (only for answered questions)
      const answerMap = new Map<number, { answer: string, correct: number }>();
      resumeData.questions.forEach(q => {
        // Only add to map if question was answered (questionAnswer is not null)
        if (q.questionAnswer != null) {
          answerMap.set(q.questionId, { answer: q.questionAnswer, correct: q.correct || 0 });
        }
      });

      // Load and restore state for answered questions only
      const answeredQuestionIds = resumeData.questions
        .filter(q => q.questionAnswer != null)
        .map(q => q.questionId);
      
      if (answeredQuestionIds.length > 0 && resumeData.subjectIds?.length) {
        await this.restService.loadAndRestoreQuestionsForResume(
          sessionId,
          resumeData.courseId,
          resumeData.subjectIds,
          answeredQuestionIds,
          answerMap
        );
      }

      // Find first unanswered question, or first question if all answered
      let targetQuestionId = allQuestionIds[0];
      const answeredSet = new Set(answeredQuestionIds);
      for (const questionId of allQuestionIds) {
        if (!answeredSet.has(questionId)) {
          targetQuestionId = questionId;
          break;
        }
      }

      // Navigate to appropriate route based on mode
      const route = resumeData.mode === 'exam' 
        ? ['/question-pool-exam/', targetQuestionId.toString(), '0']
        : ['/question-pool-study/', targetQuestionId.toString()];
      
      this.router.navigate(route);
    } catch (error) {
      LoggerService.error('Failed to resume test:', error);
      alert('An error occurred while resuming the test.');
    }
  }


  /** Navigate to New Test page with saved settings from this (finished) test; creates a new test when user starts. */
  async onRecreate(testId: number): Promise<void> {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        alert('Please log in to recreate a test.');
        return;
      }
      this.restService.clearRecreateSettings();
      this.restService.clearCurrentTestState();
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test data. The test may have been deleted.');
        return;
      }
      let topicIds: number[] | undefined;
      if (resumeData.topicIds) {
        try {
          const parsed = JSON.parse(resumeData.topicIds);
          if (Array.isArray(parsed)) topicIds = parsed;
        } catch {
          // ignore invalid JSON
        }
      }
      const settings: RecreateSettings = {
        courseId: resumeData.courseId,
        subjectIds: resumeData.subjectIds ?? [],
        mode: resumeData.mode,
        amountQuestions: resumeData.amountQuestions,
        redFlagMode: resumeData.redFlagMode,
        yellowFlagMode: resumeData.yellowFlagMode,
        greenFlagMode: resumeData.greenFlagMode,
        excludeHidden: resumeData.excludeHidden === 1,
        onlyUnseen: resumeData.onlyUnseen === 1,
        onlyWrong: resumeData.onlyWrong === 1,
        topicIds,
        seedId: resumeData.seedId
      };
      this.restService.setRecreateSettings(settings);
      this.router.navigate(['/question-pool'], { state: { recreateSettings: settings } });
    } catch (error) {
      LoggerService.error('Failed to open recreate', error);
      alert('Failed to open New Test with saved settings.');
    }
  }

  openShareDialog(test: UserTestListItem): void {
    this.shareDialogTestId = test.id;
    this.shareDialogTest = test;
    this.shareUsername = '';
    this.shareFriends = [];
    void this.loadShareFriends();
    setTimeout(() => document.getElementById('shareFriend')?.focus(), 0);
  }

  private async loadShareFriends(): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) {
      return;
    }
    this.shareFriendsLoading = true;
    try {
      this.shareFriends = await this.restService.getFriends(sessionId);
    } catch (error) {
      LoggerService.error('Failed to load friends for sharing:', error);
      this.shareFriends = [];
    } finally {
      this.shareFriendsLoading = false;
    }
  }

  closeShareDialog(): void {
    this.shareDialogTestId = null;
    this.shareDialogTest = null;
    this.shareUsername = '';
    this.shareFriends = [];
  }

  openShareError(message: string): void {
    this.shareErrorMessage = message;
  }

  closeShareError(): void {
    this.shareErrorMessage = null;
  }

  async onShareSubmit(): Promise<void> {
    if (!this.shareDialogTestId || !this.shareUsername.trim()) return;
    const username = this.shareUsername.trim();
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) return;
    this.shareSubmitLoading = true;
    try {
      const result = await this.restService.shareTest(sessionId, this.shareDialogTestId, username);
      if (result.ok) {
        this.closeShareDialog();
        await this.loadUserTests();
      } else {
        this.closeShareDialog();
        this.openShareError(result.error ?? 'Failed to share. Choose an accepted friend.');
      }
    } finally {
      this.shareSubmitLoading = false;
    }
  }

  async doStopSharing(): Promise<void> {
    const testId = this.shareDialogTestId;
    if (testId == null) return;
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) return;
    this.shareStopLoading = true;
    try {
      const ok = await this.restService.unshareTest(sessionId, testId);
      if (ok) {
        this.closeShareDialog();
        await this.loadUserTests();
      } else {
        alert('Failed to remove share.');
      }
    } finally {
      this.shareStopLoading = false;
    }
  }

  async onTestReport(testId: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        alert('Please log in to view the test report.');
        return;
      }

      // Get test details for report (similar to resume)
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test data. The test may have been deleted.');
        return;
      }

      this.restService.currentUserTestId = resumeData.userTestId;
      this.restService.currentUserTestMode = resumeData.mode;
      this.restService.courseId = resumeData.courseId;
      this.restService.subjectIds = resumeData.subjectIds ?? [];

      // Extract all question IDs from resume data
      const allQuestionIds = resumeData.questions.map(q => q.questionId);
      if (allQuestionIds.length === 0) {
        alert('No questions found for this test.');
        return;
      }

      // Set questionIds and initialize questionsMap with all questions
      this.restService.setQuestionIdsForResume(allQuestionIds);
      this.restService.setMaxQuestions(allQuestionIds.length);

      // Create answer map for restoring state
      const answerMap = new Map<number, { answer: string, correct: number }>();
      resumeData.questions.forEach(q => {
        if (q.questionAnswer != null) {
          answerMap.set(q.questionId, { answer: q.questionAnswer, correct: q.correct || 0 });
        }
      });

      // Load and restore state for all questions
      if (resumeData.subjectIds?.length) {
        await this.restService.loadAndRestoreQuestionsForResume(
          sessionId,
          resumeData.courseId,
          resumeData.subjectIds,
          allQuestionIds,
          answerMap
        );
      }

      // Navigate to results page (reportId is not actually used, but route expects it)
      this.router.navigate(['/question-pool-results', testId]);
    } catch (error) {
      LoggerService.error('Failed to load test report:', error);
      alert('An error occurred while loading the test report.');
    }
  }

  /** Tooltip for tests with multiple subjects: "Also: Subject2, Subject3" */
  getOtherSubjectsTooltip(test: UserTestListItem): string {
    if (!test.subjectNames || test.subjectNames.length <= 1) return '';
    return 'Also: ' + test.subjectNames.slice(1).join(', ');
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateString;
    }
  }

  formatDateShort(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  }

  formatScore(score: number | null): string {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(1) + '%';
  }

  formatCompleted(completedCount: number | null | undefined, totalCount: number | null | undefined): string {
    if (completedCount === null || completedCount === undefined || totalCount === null || totalCount === undefined) {
      return 'N/A';
    }
    if (completedCount === 0) {
      return 'None';
    }
    if (completedCount === totalCount) {
      return 'All';
    }
    return completedCount.toString();
  }

  isAllCompleted(completedCount: number | null | undefined, totalCount: number | null | undefined): boolean {
    if (completedCount === null || completedCount === undefined || totalCount === null || totalCount === undefined) {
      return false;
    }
    return completedCount === totalCount;
  }

  /** True if test was ended (e.g. user clicked Finish) — show Recreate; else show Resume. */
  isTestFinished(test: UserTestListItem): boolean {
    if (test.finished === true) return true;
    if (test.finished === false) return false;
    return this.isAllCompleted(test.completedCount, test.questionCount);
  }
}